import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  model,
  computed,
  booleanAttribute,
  signal, WritableSignal
} from '@angular/core';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IdService } from '../../services/id.service';
import type { ControlValueAccessor } from '@angular/forms';
import type { InputSignal, ModelSignal, Signal, InputSignalWithTransform } from '@angular/core';

export type Position = 'left' | 'right' // TODO convert to direction agnostic start/end pair

export type CheckboxStyle = 'default' | 'toggle'

export type SelectorPosition = 'center' | 'top'

// TouchTarget.PARENT needs 'position: relative;' on parent clickable element
export type TouchTarget = 'self' | 'parent'

@Component({
  selector: 'td-wb-checkbox',
  templateUrl: 'checkbox.component.html',
  styleUrls: ['checkbox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ValueAccessorProviderFactory(CheckboxComponent)],
  host: {
    '[class.td-wb-checkbox__touch-target-parent]': 'touchTarget() === "parent"'
  }
})
export class CheckboxComponent implements ControlValueAccessor {
  private static readonly ID_PREFIX: string = 'td-wb-checkbox';
  private readonly idService: IdService = inject(IdService);

  readonly checkboxId: InputSignal<string | undefined> = input<string | undefined>();
  readonly value: InputSignal<unknown> = input<unknown>();
  readonly name: InputSignal<string | undefined> = input<string | undefined>();
  readonly tabIndex: InputSignal<string | undefined> = input<string | undefined>();
  readonly labelPosition: InputSignal<Position | undefined> = input<Position | undefined>();
  readonly checkboxStyle: InputSignal<CheckboxStyle | undefined> = input<CheckboxStyle | undefined>();
  readonly selectorPosition: InputSignal<SelectorPosition> = input<SelectorPosition>('top');
  readonly touchTarget: InputSignal<TouchTarget> = input<TouchTarget>('self');
  readonly isFullWidth: InputSignal<boolean> = input<boolean>(false);
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly ariaDescribedBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-describedby' });

  readonly disabledInput: InputSignalWithTransform<boolean, unknown> = input<boolean, unknown>(false, { alias: 'disabled', transform: booleanAttribute });
  private readonly _disabledState: WritableSignal<boolean> = signal(false);
  readonly disabled: Signal<boolean> = computed(() => this.disabledInput() || this._disabledState());

  readonly checked: ModelSignal<boolean> = model<boolean>(false);

  private readonly _generatedId: string = this.idService.generateNamespaceSequentialId(CheckboxComponent.ID_PREFIX);
  readonly finalCheckboxId: Signal<string> = computed((): string => this.checkboxId() ?? this._generatedId);
  readonly finalName: Signal<string> = computed((): string => this.name() ?? `name-${this.finalCheckboxId()}`);

  readonly labelOnTheLeft: Signal<boolean> = computed((): boolean => this.labelPosition() === 'left');
  readonly toggleStyle: Signal<boolean> = computed((): boolean => this.checkboxStyle() === 'toggle');

  onChange(event: Event): void {
    event.stopPropagation();
  }

  onInputClick(event: Event): void {
    event.stopPropagation();
    if (!this.disabled()) {
      this.toggle();
      this.onChangeCallback(this.checked());
    }
  }

  toggle(): void {
    this.checked.update((checked: boolean): boolean => !checked);
  }

  hasAriaLabel: Signal<boolean> = computed((): boolean => !!(this.ariaLabel() || this.ariaLabelledBy()));

  // Implemented as part of ControlValueAccessor.
  writeValue(value: unknown): void {
    this.checked.set(Boolean(value));
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(): void {
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(isDisabled: boolean): void {
    this._disabledState.set(isDisabled);
  }

  private onChangeCallback: (_: unknown) => void =  (): void => {};
}
